import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Settings as SettingsIcon, Lock, User, Loader2, GitBranch, Download, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [passwordForm, setPasswordForm] = useState({ current: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.new_password) { toast.error('Champs requis'); return; }
    if (passwordForm.new_password !== passwordForm.confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    if (passwordForm.new_password.length < 8) { toast.error('Minimum 8 caracteres'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/auth/change-password`, { current_password: passwordForm.current, new_password: passwordForm.new_password });
      toast.success('Mot de passe modifie');
      setPasswordForm({ current: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold"><SettingsIcon className="w-6 h-6 inline mr-2" />Paramètres</h1>

      <Card>
        <CardHeader><CardTitle><User className="w-5 h-5 inline mr-2" />Mon profil</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-slate-500">Prenom</Label><p className="font-medium">{user?.prenom}</p></div>
            <div><Label className="text-slate-500">Nom</Label><p className="font-medium">{user?.nom}</p></div>
            <div><Label className="text-slate-500">Email</Label><p className="font-medium">{user?.email}</p></div>
            <div><Label className="text-slate-500">Role</Label><p className="font-medium capitalize">{user?.role}</p></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><GitBranch className="w-5 h-5 inline mr-2" />Workflows de validation</CardTitle>
          <CardDescription>Configurez le circuit de validation des factures fournisseurs (étapes, rôles, seuils auto-skip).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button onClick={() => navigate('/workflows?openTemplates=1')} className="w-full justify-between bg-emerald-600 hover:bg-emerald-700">
            <span className="flex items-center"><Download className="w-4 h-4 mr-2" /> Partir d'un modèle de workflow</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => navigate('/workflows')} className="w-full justify-between">
            <span className="flex items-center"><GitBranch className="w-4 h-4 mr-2" /> Gérer mes workflows</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle><Lock className="w-5 h-5 inline mr-2" />Changer le mot de passe</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Mot de passe actuel</Label><Input type="password" value={passwordForm.current} onChange={e => setPasswordForm({...passwordForm, current: e.target.value})} /></div>
          <div><Label>Nouveau mot de passe</Label><Input type="password" value={passwordForm.new_password} onChange={e => setPasswordForm({...passwordForm, new_password: e.target.value})} /></div>
          <div><Label>Confirmer</Label><Input type="password" value={passwordForm.confirm} onChange={e => setPasswordForm({...passwordForm, confirm: e.target.value})} /></div>
          <Button onClick={handleChangePassword} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Modifier le mot de passe</Button>
        </CardContent>
      </Card>
    </div>
  );
}
